import { Routes } from '@angular/router';
import { UserRole } from './core/models/enums';
import { activationGuard } from './core/guards/activation.guard';
import { authGuard } from './core/guards/auth-guard';
import { ccModuleGuard } from './core/guards/cc-module.guard';
import { roleGuard } from './core/guards/role.guard';
import { subcategoriasHabilitadasGuard } from './core/guards/subcategories.guard';
import { featureFlagGuard } from './core/guards/feature-flag.guard';
import { MainLayoutComponent } from './core/layout/main-layout';
import { AdminLayoutComponent } from './core/layout/admin-layout/admin-layout';
import { HomeComponent } from './features/home';

export const routes: Routes = [
    // Redirección por defecto a Login
    { path: '', redirectTo: 'auth/login', pathMatch: 'full' },

    // Auth Routes (No layout constraints)
    {
        path: 'auth/login',
        title: '',
        loadComponent: () => import('./core/auth/login').then(m => m.LoginComponent)
    },
    // Registro deshabilitado temporalmente: alta de cuentas solo por invitación en esta etapa.
    // {
    //     path: 'auth/register',
    //     title: '',
    //     loadComponent: () => import('./core/auth/register').then(m => m.RegisterComponent)
    // },
    {
        path: 'activation-pending',
        title: '',
        loadComponent: () => import('./core/auth/activation-pending/activation-pending').then(m => m.ActivationPendingComponent)
    },
    {
        path: 'auth/forgot-password',
        title: 'Recuperar Contraseña',
        loadComponent: () => import('./core/auth/forgot-password/forgot-password').then(m => m.ForgotPasswordComponent)
    },
    {
        path: 'auth/set-password',
        title: 'Establecer Contraseña',
        loadComponent: () => import('./core/auth/set-password/set-password').then(m => m.SetPasswordComponent)
    },

    // Public Layout
    {
        path: '',
        component: MainLayoutComponent,
        children: [
            { path: '', component: HomeComponent },

            // Public Booking Form
            { path: 'booking', loadComponent: () => import('./features/booking/booking-form').then(m => m.BookingFormComponent) },
        ]
    },

    // Admin & Superadmin Layout (Protected)
    {
        path: '',
        component: AdminLayoutComponent,
        canActivate: [authGuard],
        children: [
            {
                path: 'dashboard',
                title: 'Panel',
                canActivate: [activationGuard],
                loadComponent: () => import('./features/dashboard/user-dashboard').then(m => m.UserDashboardComponent)
            },
            {
                path: 'admin/dashboard',
                title: 'Dashboard de Administración',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/admin/admin-dashboard').then(m => m.AdminDashboardComponent)
            },
            {
                path: 'branches',
                title: 'Gestión de Sucursales',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/branches/main/branch-main').then(m => m.BranchMainComponent)
            },
            {
                path: 'branches/new',
                title: 'Gestión de Sucursal',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/branches/new/branch-new').then(m => m.BranchNewComponent)
            },
            {
                path: 'branches/:id',
                title: 'Gestión de Sucursal',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/branches/edit/branch-edit').then(m => m.BranchEditComponent)
            },
            {
                path: 'admin/integrations/clover/callback',
                title: 'Conectando con Clover',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/integrations/clover-callback/clover-callback').then(m => m.CloverCallbackComponent)
            },
            {
                path: 'business',
                title: 'Gestion de empresas',
                canActivate: [roleGuard([UserRole.SUPERADMIN])],
                loadComponent: () => import('./features/business/main/business-main').then(m => m.BusinessMainComponent)
            },
            {
                path: 'business/new',
                title: 'Gestion Empresa',
                canActivate: [roleGuard([UserRole.SUPERADMIN])],
                loadComponent: () => import('./features/business/new/business-new').then(m => m.BusinessNewComponent)
            },
            {
                path: 'business/:id',
                title: 'Gestión de Empresa',
                canActivate: [roleGuard([UserRole.SUPERADMIN])],
                loadComponent: () => import('./features/business/edit/business-edit').then(m => m.BusinessEditComponent)
            },
            {
                path: 'admin/my-organization',
                title: 'Mi Organización',
                canActivate: [
                    roleGuard([UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/admin/my-organization/my-organization').then(m => m.MyOrganizationComponent)
            },
            {
                path: 'staff',
                title: 'Gestión de Staff',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/staff/staff-main/staff-main').then(m => m.StaffMainComponent)
            },
            {
                path: 'staff/new',
                title: 'Gestión de Staff',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/staff/staff-new/staff-new').then(m => m.StaffNewComponent)
            },
            {
                path: 'staff/:id/manage',
                title: 'Gestión de Horarios y Servicios',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/staff-manage/staff-manage').then(m => m.StaffManageComponent)
            },
            {
                path: 'staff/:id',
                title: 'Gestión de Staff',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/staff/staff-edit/staff-edit').then(m => m.StaffEditComponent)
            },
            {
                path: 'resources',
                title: 'Gestión de Recursos',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'resources' },
                loadComponent: () => import('./features/resources/main/resource-main').then(m => m.ResourceMainComponent)
            },
            {
                path: 'resources/new',
                title: 'Gestión de Recursos',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'resources' },
                loadComponent: () => import('./features/resources/new/resource-new').then(m => m.ResourceNewComponent)
            },
            {
                path: 'resources/edit/:id',
                title: 'Gestión de Recursos',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'resources' },
                loadComponent: () => import('./features/resources/edit/resource-edit').then(m => m.ResourceEditComponent)
            },
            {
                path: 'services',
                title: 'Gestión de Servicios',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'services' },
                loadComponent: () => import('./features/service/main/service-main').then(m => m.ServiceMainComponent)
            },
            {
                path: 'services/new',
                title: 'Gestión de Servicios',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'services' },
                loadComponent: () => import('./features/service/new/service-new').then(m => m.ServiceNewComponent)
            },
            {
                path: 'services/edit/:id',
                title: 'Gestión de Servicios',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'services' },
                loadComponent: () => import('./features/service/edit/service-edit').then(m => m.ServiceEditComponent)
            },
            {
                path: 'admin/my-agenda',
                title: 'Mi Agenda',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'booking' },
                loadComponent: () => import('./features/agenda/main/agenda-main').then(m => m.AgendaMainComponent)
            },
            {
                path: 'admin/clients',
                title: 'Mis Clientes (CRM)',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/clients/main/client-main').then(m => m.ClientMainComponent)
            },
            {
                path: 'admin/clients/new',
                title: 'Nuevo Cliente',
                canActivate: [roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER])],
                loadComponent: () => import('./features/clients/new/client-new').then(m => m.ClientNewComponent)
            },
            {
                path: 'admin/clients/:id',
                title: 'Ficha 360° del Cliente',
                canActivate: [roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER])],
                loadComponent: () => import('./features/clients/detail/client-detail').then(m => m.ClientDetailComponent)
            },
            {
                path: 'clientes/cc',
                title: 'KronoFlow - Cuentas Corrientes',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    ccModuleGuard
                ],
                loadComponent: () => import('./features/clientes/clientes-cc-list/clientes-cc-list').then(m => m.ClientesCcListComponent)
            },
            {
                path: 'clientes/cc/:clientId',
                title: 'KronoFlow - Estado de Cuenta Corriente',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    ccModuleGuard
                ],
                loadComponent: () => import('./features/clientes/cliente-cc-dashboard/cliente-cc-dashboard').then(m => m.ClienteCcDashboardComponent)
            },
            {
                path: 'admin/inventory',
                title: 'Inventario',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'products' },
                children: [
                    {
                        path: '',
                        title: 'Productos',
                        loadComponent: () => import('./features/inventory/main/inventory-main').then(m => m.InventoryMainComponent)
                    },
                    {
                        path: 'categories',
                        title: 'Categorías',
                        loadComponent: () => import('./features/categories/main/category-main').then(m => m.CategoryMainComponent)
                    },
                    {
                        path: 'subcategories',
                        title: 'Subcategorías',
                        canActivate: [subcategoriasHabilitadasGuard],
                        loadComponent: () => import('./features/subcategories/main/subcategory-main').then(m => m.SubcategoryMainComponent)
                    },
                    {
                        path: 'new',
                        title: 'Gestión de Producto',
                        loadComponent: () => import('./features/inventory/new/inventory-new').then(m => m.InventoryNewComponent)
                    },
                    {
                        path: 'edit/:id',
                        title: 'Gestión de Producto',
                        loadComponent: () => import('./features/inventory/edit/inventory-edit').then(m => m.InventoryEditComponent)
                    },
                    {
                        path: 'returns/:id',
                        title: 'Devolución de Venta',
                        loadComponent: () => import('./features/inventory/returns/inventory-returns').then(m => m.InventoryReturnsComponent)
                    }
                ]
            },
            {
                path: 'admin/inventory/distribution',
                title: 'Distribución de Inventario',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'distribution' },
                loadComponent: () => import('./features/inventory/distribution/inventory-distribution').then(m => m.InventoryDistributionComponent)
            },
            {
                path: 'admin/inventory/distribution/tracking',
                title: 'Distribuciones',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'distribution' },
                loadComponent: () => import('./features/inventory/distribution/tracking/inventory-distribution-tracking').then(m => m.InventoryDistributionTrackingComponent)
            },
            {
                path: 'admin/suppliers',
                title: 'Proveedores',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/main/supplier-main').then(m => m.SupplierMainComponent)
            },
            {
                path: 'admin/suppliers/cc',
                title: 'Cuentas Corrientes de Proveedores',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/supplier-cc-list/supplier-cc-list').then(m => m.SupplierCcListComponent)
            },
            {
                path: 'admin/suppliers/:supplierId/cuenta-corriente',
                title: 'Cuenta Corriente del Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/supplier-cc-dashboard/supplier-cc-dashboard').then(m => m.SupplierCcDashboardComponent)
            },
            {
                path: 'admin/suppliers/new',
                title: 'Nuevo Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/new/supplier-new').then(m => m.SupplierNewComponent)
            },
            {
                path: 'admin/suppliers/:id/edit',
                title: 'Editar Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/edit/supplier-edit').then(m => m.SupplierEditComponent)
            },
            {
                path: 'admin/suppliers/:id/receipts',
                title: 'Recepciones del Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/supplier-receipts/list/supplier-receipt-list').then(m => m.SupplierReceiptListComponent)
            },
            {
                path: 'admin/suppliers/:id/aliases',
                title: 'Alias del Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/suppliers/alias-list/supplier-alias-list').then(m => m.SupplierAliasListComponent)
            },
            {
                path: 'admin/supplier-receipts/new',
                title: 'Nueva Recepción de Proveedor',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/supplier-receipts/new/supplier-receipt-new').then(m => m.SupplierReceiptNewComponent)
            },
            {
                path: 'admin/supplier-receipts/:id/reconcile',
                title: 'Conciliación de Recepción',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/supplier-receipts/reconcile/supplier-receipt-reconcile').then(m => m.SupplierReceiptReconcileComponent)
            },
            {
                path: 'admin/supplier-receipts/:id/detail',
                title: 'Registro Histórico de Recepción',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'suppliers' },
                loadComponent: () => import('./features/supplier-receipts/detail/supplier-receipt-detail').then(m => m.SupplierReceiptDetailComponent)
            },
            {
                path: 'admin/sales',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'sales' },
                children: [
                    {
                        path: '',
                        title: 'Ventas Realizadas',
                        loadComponent: () => import('./features/sales/main/sale-main').then(m => m.SaleMainComponent)
                    },
                    {
                        path: 'new',
                        title: 'Nueva Venta',
                        loadComponent: () => import('./features/sales/new/sale-new').then(m => m.SaleNewComponent)
                    },
                    {
                        path: 'vouchers',
                        title: 'Vales de saldo a favor',
                        loadComponent: () => import('./features/sales/vouchers/voucher-main').then(m => m.VoucherMainComponent)
                    },
                    {
                        path: ':id',
                        title: 'Detalle de Venta',
                        loadComponent: () => import('./features/sales/detail/sale-detail').then(m => m.SaleDetailComponent)
                    }
                ]
            },
            {
                path: 'billing',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'billing' },
                children: [
                    {
                        path: '',
                        title: 'KronoFlow - Facturación Electrónica',
                        loadComponent: () => import('./features/billing/invoice-main/invoice-main').then(m => m.InvoiceMainComponent)
                    },
                    {
                        path: 'new',
                        title: 'KronoFlow - Nueva Factura',
                        loadComponent: () => import('./features/billing/new/invoice-new').then(m => m.InvoiceNewComponent)
                    },
                    {
                        path: 'edit/:id',
                        title: 'KronoFlow - Reintentar Factura',
                        loadComponent: () => import('./features/billing/edit/invoice-edit').then(m => m.InvoiceEditComponent)
                    },
                    {
                        path: 'detail/:id',
                        title: 'KronoFlow - Detalle de Factura',
                        loadComponent: () => import('./features/billing/detail/invoice-detail').then(m => m.InvoiceDetailComponent)
                    },
                    {
                        path: 'credit-note-new/:id',
                        title: 'KronoFlow - Emitir Nota de Crédito',
                        loadComponent: () => import('./features/billing/credit-note-new/credit-note-new').then(m => m.CreditNoteNewComponent)
                    },
                    {
                        path: 'recurring',
                        title: 'KronoFlow - Facturación Programada',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/billing/invoice-schedule-main/invoice-schedule-main').then(m => m.InvoiceScheduleMainComponent)
                    },
                    {
                        path: 'recurring/new',
                        title: 'KronoFlow - Nueva Plantilla Recurrente',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/billing/invoice-schedule-form/invoice-schedule-form').then(m => m.InvoiceScheduleFormComponent)
                    },
                    {
                        path: 'recurring/edit/:id',
                        title: 'KronoFlow - Editar Plantilla Recurrente',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/billing/invoice-schedule-form/invoice-schedule-form').then(m => m.InvoiceScheduleFormComponent)
                    }
                ]
            },
            {
                path: 'finance/cash',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'finance' },
                children: [
                    {
                        path: 'daily',
                        title: 'Caja Diaria',
                        loadComponent: () => import('./features/finance/cash-daily/cash-daily').then(m => m.CashDailyComponent)
                    },
                    {
                        path: 'history',
                        title: 'Historial de Cajas',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/finance/cash-main/cash-main').then(m => m.CashMainComponent)
                    },
                    {
                        path: 'movements',
                        title: 'Movimientos Financieros',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/finance/movements/finance-movements').then(m => m.FinanceMovementsComponent)
                    },
                    {
                        path: 'shift-audit',
                        title: 'Auditoría de Turnos',
                        canActivate: [
                            roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER])
                        ],
                        loadComponent: () => import('./features/finance/cash-shift/cash-shift-audit/cash-shift-audit').then(m => m.CashShiftAuditComponent)
                    },
                    { path: '', redirectTo: 'daily', pathMatch: 'full' }
                ]
            },
            {
                path: 'finance/expenses',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'finance' },
                children: [
                    {
                        path: 'templates',
                        title: 'Plantillas de Costos Fijos',
                        loadComponent: () => import('./features/finance/expenses/expenses-templates/expenses-templates').then(m => m.ExpensesTemplatesComponent)
                    },
                    {
                        path: 'monthly',
                        title: 'Gastos del Mes',
                        loadComponent: () => import('./features/finance/expenses/expenses-monthly/expenses-monthly').then(m => m.ExpensesMonthlyComponent)
                    },
                    { path: '', redirectTo: 'monthly', pathMatch: 'full' }
                ]
            },

            {
                path: 'admin/payment-methods',
                title: 'Formas de Pago',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/payment-methods/payment-methods-main/payment-methods-main').then(m => m.PaymentMethodsMainComponent)
            },
            {
                path: 'admin/payment-methods/new',
                title: 'Nueva Forma de Pago',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/payment-methods/new/payment-method-new').then(m => m.PaymentMethodNewComponent)
            },
            {
                path: 'admin/payment-methods/edit/:id',
                title: 'Editar Forma de Pago',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/payment-methods/edit/payment-method-edit').then(m => m.PaymentMethodEditComponent)
            },
            {
                path: 'admin/config/categories',
                title: 'Categorías de Caja',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/config/categories/main/categories-main').then(m => m.CategoriesMainComponent)
            },
            {
                path: 'admin/config/administrative-categories',
                title: 'Categorías de Gastos Administrativos',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER]),
                    activationGuard
                ],
                loadComponent: () => import('./features/config/administrative-categories/main/administrative-categories-main').then(m => m.AdministrativeCategoriesMainComponent)
            },
            {
                path: 'forms',
                title: 'Gestión de Formularios Dinámicos',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'forms' },
                loadComponent: () => import('./features/forms/main/forms-main').then(m => m.FormsMainComponent)
            },
            {
                path: 'forms/new',
                title: 'Nueva Plantilla de Formulario',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'forms' },
                loadComponent: () => import('./features/forms/new/forms-new').then(m => m.FormsNewComponent)
            },
            {
                path: 'forms/edit/:id',
                title: 'Editar Plantilla de Formulario',
                canActivate: [
                    roleGuard([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER]),
                    activationGuard,
                    featureFlagGuard
                ],
                data: { requiredFeature: 'forms' },
                loadComponent: () => import('./features/forms/edit/forms-edit').then(m => m.FormsEditComponent)
            }
        ]
    },

    // Tenant (Business) Home - Catch-all for slugs (MUST BE LAST)
    {
        path: ':slug',
        component: MainLayoutComponent,
        children: [
            {
                path: '',
                loadComponent: () => import('./features/business/business-home').then(m => m.BusinessHomeComponent)
            }
        ]
    },
    { path: '**', redirectTo: '' }


];
